<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, watch } from 'vue';
import Hls from 'hls.js';

const props = defineProps<{
    videoUrl: string;
}>();

const emit = defineEmits<{
    (e: 'play'): void;
}>();

const videoRef = ref<HTMLVideoElement | null>(null);
let hls: Hls | null = null;

// Convert Cloudflare watch/iframe URL to HLS manifest
// e.g. https://customer-xxx.cloudflarestream.com/{id}/watch → .../manifest/video.m3u8
const toHlsUrl = (url: string): string =>
    url.replace(/\/watch$/, '/manifest/video.m3u8');

const initPlayer = () => {
    if (!videoRef.value || !props.videoUrl) return;

    destroyPlayer();

    const hlsUrl = toHlsUrl(props.videoUrl);

    if (Hls.isSupported()) {
        hls = new Hls();
        hls.loadSource(hlsUrl);
        hls.attachMedia(videoRef.value);
    } else if (videoRef.value.canPlayType('application/vnd.apple.mpegurl')) {
        // Safari has native HLS support
        videoRef.value.src = hlsUrl;
    }
};

const destroyPlayer = () => {
    if (hls) {
        hls.destroy();
        hls = null;
    }
};

onMounted(() => initPlayer());

watch(() => props.videoUrl, () => initPlayer());

onBeforeUnmount(() => destroyPlayer());
</script>

<template>
    <video
        ref="videoRef"
        controls
        preload="metadata"
        class="cloudflare-player"
        @play="emit('play')"
    />
</template>

<style lang="scss" scoped>
@use 'certifid-v2-client-hub-styles' as *;

.cloudflare-player {
    width: 100%;
    border-radius: $portal-card-radius;
    overflow: hidden;
    display: block;
}
</style>
